/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Structure:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }

==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import store from "@/store/store";
import helpers from "@/utils/helpers";
import constant from "./utils/constant";
import { APP_ROUTE, PAGES_PATH, PAGES_NAME } from "./utils/route";

Vue.use(Router);

const pages_login_path = "/pages/login";
const pages_cbs_path = APP_ROUTE.MAIN_APP_ROUTE_PATH;
const page_notifications = PAGES_PATH.PAGE_NOTIFICATIONS_PATH;
const pages_cbs_post_path = PAGES_PATH.PAGE_CBS_POST_PATH;
const pages_cbs_note_path = PAGES_PATH.PAGE_CBS_NOTE_PATH;
const pages_cbs_todo_path = PAGES_PATH.PAGE_CBS_TODO_PATH;
const pages_cbs_login_path = PAGES_PATH.PAGE_CBS_LOGIN_PATH;
const pages_tuyen_sinh_path = PAGES_PATH.PAGE_TUYEN_SINH_PATH;
const pages_cmd_view_2025_path = PAGES_PATH.PAGE_CMD_VIEW_2025_PATH;
const pages_cbs_change_role_path = PAGES_PATH.PAGE_CBS_CHANGE_ROLE_PATH;
const pages_cbs_device_path = PAGES_PATH.PAGE_CBS_DEVICE_PATH;
const pages_cbs_request_path = PAGES_PATH.PAGE_CBS_REQUEST_PATH;
const pages_cbs_employee_path = PAGES_PATH.PAGE_CBS_EMPLOYEE_PATH;
const pages_cbs_mission_of_day = PAGES_PATH.PAGE_MISSION_OF_DAY_PATH;
const pages_cbs_request_types_path = PAGES_PATH.PAGE_CBS_REQUEST_TYPES_PATH;
const pages_cbs_request_types_config_path = PAGES_PATH.PAGE_CBS_REQUEST_TYPES_CONFIG_PATH;
const pages_cbs_user_guide_path = PAGES_PATH.PAGE_CBS_USER_GUIDE;
const pages_cbs_user_guide_posts_path = PAGES_PATH.PAGE_CBS_USER_GUIDE_POST_PATH;

// const pages_link_path = '/link';
// const pages_card_path = '/card';

const current_app_path = helpers.currentAppPath();
const current_app_login_path = `${current_app_path}/login`;
const current_app_register_path = `${current_app_path}/register`;

const router = new Router({
	mode: "history",
	base: "/", // process.env.BASE_URL,
	scrollBehavior(to, from) {
		// prevent scroll top on click view detail todo
		if ([PAGES_NAME.PAGE_CBS_TODO_NAME, PAGES_NAME.PAGE_CBS_MY_TODO_NAME].includes(to.name) && to.name === from.name) {
			const toQuery = { ...to.query };
			const fromQuery = { ...from.query };
			delete toQuery.id;
			delete fromQuery.id;
			delete toQuery.is_child;
			delete fromQuery.is_child;
			if (JSON.stringify(toQuery) === JSON.stringify(fromQuery)) {
				return;
			}
		}
		return { x: 0, y: 0 };
	},
	routes: [
		{
			// =============================================================================
			// MAIN LAYOUT ROUTESs
			// =============================================================================
			path: "",
			component: () => import("./layouts/main/Main.vue"),
			children: [
				// =============================================================================
				// Theme Routes
				// =============================================================================
				{
					path: "/",
					redirect: current_app_path
				},

				{
					path: "/welcome",
					redirect: "/pages/comingsoon"
				},

				// =============================================================================
				// Application Routes
				// =============================================================================
				{
					path: pages_cbs_path,
					name: "cbs-dashboard",
					component: () => import("./views/apps/cbs/dashboard/Analytics.vue"),
					meta: {
						authRequired: true,
						rule: "member"
					}
				},
				// =============================================================================
				// Device
				// =============================================================================
				{
					path: pages_cbs_device_path,
					name: PAGES_NAME.PAGE_CBS_DEVICE_NAME,
					component: () => import("./views/apps/cbs/device/DeviceManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.DEVICE.CAN_VIEW,
						authRequired: true,
						pageTitle: "DevicesList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_DEVICE_BY_CATEGORY_PATH,
					name: PAGES_NAME.PAGE_CBS_DEVICE_BY_CATEGORY_NAME,
					component: () => import("./views/apps/cbs/device/DeviceManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.DEVICE.CAN_VIEW,
						authRequired: true,
						rule: "member"
					}
				},
				// =============================================================================
				// Employee
				// =============================================================================
				{
					path: pages_cbs_employee_path,
					name: PAGES_NAME.PAGE_CBS_EMPLOYEE_NAME,
					component: () => import("./views/apps/cbs/employee/EmployeeManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.EMPLOYEE.CAN_VIEW,
						authRequired: true,
						pageTitle: "EmployeesList",
						rule: "member"
					}
				},
				// =============================================================================
				// economic_contracts
				// =============================================================================
				{
					path: PAGES_PATH.PAGE_CBS_ECONOMIC_CONTRACT_PATH,
					name: PAGES_NAME.PAGE_CBS_ECONOMIC_CONTRACT_NAME,
					component: () => import("./views/apps/economic-contract/EconomicContractManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.ECONOMIC_CONTRACT.CAN_VIEW,
						authRequired: true,
						pageTitle: "EconomicContractsList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_EMPLOYEE_BY_DEPARTMENT_PATH,
					name: PAGES_NAME.PAGE_CBS_EMPLOYEE_BY_DEPARTMENT_NAME,
					component: () => import("./views/apps/cbs/employee/EmployeeManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.EMPLOYEE.CAN_VIEW,
						authRequired: true,
						rule: "member"
					}
				},
				// =============================================================================
				// Profile
				// =============================================================================
				{
					path: constant.PATH.PROFILE,
					name: "profile",
					component: () => import("./views/pages/profile/UserSettings.vue"),
					meta: {
						authRequired: true,
						pageTitle: "Profile",
						rule: "member"
					}
				},
				// =============================================================================
				// Profile student
				// =============================================================================
				{
					path: constant.PATH.PROFILE_STUDENT,
					name: "student-profile",
					component: () => import("./views/pages/profile/student/UserStudentProfile.vue"),
					meta: {
						authRequired: true,
						pageTitle: "StudentProfile",
						rule: "member"
					}
				},
				// =============================================================================
				// Todo
				// =============================================================================
				{
					path: pages_cbs_todo_path,
					name: PAGES_NAME.PAGE_CBS_TODO_NAME,
					component: () => import("./views/apps/cbs/todo-list/TodoListManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TODOLIST.CAN_VIEW,
						authRequired: false,
						pageTitle: "TodoListManagement",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_MY_TODO_PATH,
					name: PAGES_NAME.PAGE_CBS_MY_TODO_NAME,
					component: () => import("./views/apps/cbs/todo-list/TodoListManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TODOLIST.CAN_VIEW,
						authRequired: false,
						pageTitle: "MyTodoList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_MY_CREATED_TODO_PATH,
					name: PAGES_NAME.PAGE_CBS_MY_CREATED_TODO_NAME,
					component: () => import("./views/apps/cbs/todo-list/TodoListManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TODOLIST.CAN_VIEW,
						authRequired: false,
						pageTitle: "MyCreatedTodoList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_MY_FOLLOWING_TODO_PATH,
					name: PAGES_NAME.PAGE_CBS_MY_FOLLOWING_TODO_NAME,
					component: () => import("./views/apps/cbs/todo-list/TodoListManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TODOLIST.CAN_VIEW,
						authRequired: false,
						pageTitle: "MyFollowingTodoList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_MY_SCHEDULE_TODO_PATH,
					name: PAGES_NAME.PAGE_CBS_MY_SCHEDULE_TODO_NAME,
					component: () => import("./views/apps/cbs/todo-list/TodoListManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TODOLIST.CAN_VIEW,
						authRequired: false,
						pageTitle: "MyScheduleTodoList",
						rule: "member"
					}
				},
				// =============================================================================
				// Request
				// =============================================================================
				{
					path: pages_cbs_request_path,
					name: PAGES_NAME.PAGE_CBS_REQUEST_NAME,
					component: () => import("./views/apps/cbs/request-v2/RequestList.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REQUEST.CAN_VIEW,
						authRequired: true,
						pageTitle: "RequestsManagement",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_REQUESTS_CREATED_BY_ME_PATH,
					name: PAGES_NAME.PAGE_CBS_REQUESTS_CREATED_BY_ME_NAME,
					component: () => import("./views/apps/cbs/request-v2/RequestList.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REQUEST.CAN_VIEW,
						authRequired: true,
						pageTitle: "MyCreatedRequests",
						rule: "member"
					}
				},
				// {
				// 	path: PAGES_PATH.PAGE_CBS_REQUESTS_RELATING_TO_ME_PATH,
				// 	name: PAGES_NAME.PAGE_CBS_REQUESTS_RELATING_TO_ME_NAME,
				// 	component: () => import("./views/apps/cbs/request-v2/RequestList.vue"),
				// 	meta: {
				// 		permissionKey: constant.PERMISSION.REQUEST.CAN_VIEW,
				// 		authRequired: true,
				// 		pageTitle: "MyRelatingRequests",
				// 		rule: "member"
				// 	}
				// },
				{
					path: PAGES_PATH.PAGE_CBS_REQUESTS_APPROVE_BY_ME_PATH,
					name: PAGES_NAME.PAGE_CBS_REQUESTS_APPROVE_BY_ME_NAME,
					component: () => import("./views/apps/cbs/request-v2/RequestList.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REQUEST.CAN_VIEW,
						authRequired: true,
						pageTitle: "ApproveByMeRequests",
						rule: "member"
					}
				},
				{
					path: pages_cbs_request_types_path,
					name: PAGES_NAME.PAGE_CBS_REQUEST_TYPES_NAME,
					component: () => import("./views/apps/cbs/request-types/RequestTypeManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REQUEST_TYPE.CAN_VIEW,
						authRequired: true,
						pageTitle: "ConfigRequestType",
						rule: "member"
					}
				},
				{
					path: pages_cbs_request_types_config_path,
					name: PAGES_NAME.PAGE_CBS_REQUEST_TYPES_CONFIG_NAME,
					component: () => import("./views/apps/cbs/request-types/RequestTypeConfig.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REQUEST_TYPE.CAN_VIEW,
						authRequired: true,
						pageTitle: "ManageCustomerClassification",
						rule: "member",
						parent: PAGES_NAME.PAGE_CBS_REQUEST_TYPES_NAME
					}
				},
				// Notifications
				// =============================================================================
				{
					path: page_notifications,
					name: PAGES_NAME.PAGE_NOTIFICATIONS_NAME,
					component: () => import("./views/apps/notifications/PageNotifications.vue"),
					meta: {
						authRequired: true,
						pageTitle: "Notifications",
						rule: "member"
					}
				},
				// =============================================================================
				// Note
				// =============================================================================
				{
					path: pages_cbs_note_path,
					name: PAGES_NAME.PAGE_CBS_NOTE_NAME,
					component: () => import("./views/apps/cbs/note/NoteManagement.vue"),
					meta: {
						pageTitle: "NotesList",
						rule: "member"
					}
				},
				// =============================================================================
				// Post
				// =============================================================================
				{
					path: pages_cbs_post_path,
					name: PAGES_NAME.PAGE_CBS_POST_NAME,
					component: () => import("./views/apps/cbs/post/PostManagement.vue"),
					meta: {
						pageTitle: "PostsManagement",
						rule: "member"
					}
				},
				// Alarms
				{
					path: PAGES_PATH.PAGE_CBS_ALARM_PATH,
					name: PAGES_NAME.PAGE_CBS_ALARM_NAME,
					component: () => import("./views/apps/cbs/alarm/AlarmsList.vue"),
					meta: {
						permissionKey: constant.PERMISSION.ALARM.VIEW,
						pageTitle: "List",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_ALARM_SETTING_PATH,
					name: PAGES_NAME.PAGE_CBS_ALARM_SETTING_NAME,
					component: () => import("./views/apps/cbs/alarm-setting/AlarmSettingList.vue"),
					meta: {
						pageTitle: "List",
						rule: "member"
					}
				},
				// =============================================================================
				// Mission of day
				// =============================================================================
				{
					path: pages_cbs_mission_of_day,
					name: PAGES_NAME.PAGE_MISSION_OF_DAY_NAME,
					component: () => import("./views/apps/cbs/mission-of-day/MissionDay.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TODOLIST.CAN_VIEW,
						authRequired: true,
						pageTitle: "MissionOfDay",
						rule: "member"
					}
				},
				// =============================================================================
				// Report form
				// =============================================================================
				{
					path: PAGES_PATH.PAGE_CBS_REPORT_FORM_PATH,
					name: PAGES_NAME.PAGE_CBS_REPORT_FORM_NAME,
					component: () => import("./views/apps/cbs/report-form/ReportFormList.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REPORT_FORM.CAN_VIEW,
						authRequired: true,
						pageTitle: "ReportFormList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_REPORT_FORM_CONFIG_PATH,
					name: PAGES_NAME.PAGE_CBS_REPORT_FORM_CONFIG_NAME,
					component: () => import("./views/apps/cbs/report-form/ReportFormConfig.vue"),
					meta: {
						permissionKey: constant.PERMISSION.REPORT_FORM.CAN_VIEW,
						authRequired: true,
						// pageTitle: "ConfigReportForm",
						rule: "member",
						parent: PAGES_NAME.PAGE_CBS_REPORT_FORM_NAME
					}
				},
				// Customer
				// =============================================================================
				{
					path: PAGES_PATH.PAGE_CBS_CUSTOMER_PATH,
					name: PAGES_NAME.PAGE_CBS_CUSTOMER_NAME,
					component: () => import("./views/apps/cbs/customer/CustomerManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.CUSTOMER.CAN_VIEW,
						authRequired: true,
						pageTitle: "CustomersList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_CUSTOMER_DEBPT_REPORTING_PATH,
					name: PAGES_NAME.PAGE_CBS_CUSTOMER_DEBPT_REPORTING,
					component: () => import("./views/apps/cbs/customer/CustomerDebtReporting.vue"),
					meta: {
						permissionKey: constant.PERMISSION.CUSTOMER.CAN_VIEW,
						authRequired: true,
						pageTitle: "CustomerDebtReporting",
						rule: "member",
						parent: PAGES_NAME.PAGE_CBS_CUSTOMER_NAME
					}
				},
				// Customer Products
				{
					path: PAGES_PATH.PAGE_CBS_CUSTOMER_LIST_PRODUCTS_PATH,
					name: PAGES_NAME.PAGE_CBS_CUSTOMER_LIST_PRODUCTS_NAME,
					component: () => import("./views/apps/cbs/customer/CustomerListProducts.vue"),
					meta: {
						permissionKey: constant.PERMISSION.PRODUCT.CAN_VIEW,
						authRequired: true,
						pageTitle: "Products",
						rule: "member",
						parent: PAGES_NAME.PAGE_CBS_CUSTOMER_NAME
					}
				},
				// Customer Qoute
				// {
				// 	path: PAGES_PATH.PAGE_CBS_CUSTOMER_LIST_PRODUCTS_PATH,
				// 	name: PAGES_NAME.PAGE_CBS_CUSTOMER_LIST_PRODUCTS_NAME,
				// 	component: () => import("./views/apps/cbs/customer/CustomerListProducts.vue"),
				// 	meta: {
				// 		permissionKey: constant.PERMISSION.PRODUCT.CAN_VIEW,
				// 		authRequired: true,
				// 		pageTitle: "Products",
				// 		rule: "member",
				// 		parent: PAGES_NAME.PAGE_CBS_CUSTOMER_NAME
				// 	}
				// },
				// =============================================================================
				// User Guide
				// =============================================================================
				{
					path: pages_cbs_user_guide_path,
					name: PAGES_NAME.PAGE_CBS_USER_GUIDE,
					component: () => import("./views/apps/cbs/user-guide/GuideCategories.vue"),
					meta: {
						pageTitle: "UserGuide",
						rule: "member"
					}
				},
				// =============================================================================
				// User Guide Posts
				// =============================================================================
				{
					path: pages_cbs_user_guide_posts_path,
					name: PAGES_NAME.PAGE_CBS_USER_GUIDE_POSTS,
					component: () => import("./views/apps/cbs/user-guide/GuidePosts.vue"),
					meta: {
						// breadcrumb: [ { title: "UserGuide", url: pages_cbs_user_guide_posts_path  }, { active: true }],
						// pageTitle: "UserGuide",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_TAG_LIST_PATH,
					name: PAGES_NAME.PAGE_CBS_TAG_LIST_NAME,
					component: () => import("./views/apps/cbs/tag/TagList.vue"),
					meta: {
						permissionKey: constant.PERMISSION.TAG.VIEW,
						authRequired: true,
						pageTitle: "TagList",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_CUSTOMER_CLASSIFICATION_PATH,
					name: PAGES_NAME.PAGE_CBS_CUSTOMER_CLASSIFICATION_NAME,
					component: () => import("./views/apps/cbs/customer/CustomerClassification.vue"),
					meta: {
						permissionKey: constant.PERMISSION.PIPELINE.VIEW,
						authRequired: true,
						pageTitle: "ManageCustomerClassification",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_INVENTORY_PATH,
					name: PAGES_NAME.PAGE_CBS_INVENTORY_NAME,
					component: () => import("./views/apps/cbs/inventory/InventoryManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.INVENTORY.CAN_VIEW,
						authRequired: true,
						pageTitle: "InventoriesManagement",
						rule: "member"
					}
				},
				{
					path: PAGES_PATH.PAGE_CBS_KCS_PATH,
					name: PAGES_NAME.PAGE_CBS_KCS_NAME,
					component: () => import("./views/apps/cbs/inventory/KCSManagement.vue"),
					meta: {
						// permissionKey: constant.PERMISSION.KCS.CAN_VIEW,
						authRequired: true,
						rule: "member"
					}
				},
				// =============================================================================
				// Event Confirmation
				// =============================================================================
				{
					path: PAGES_PATH.PAGE_CBS_EVENT_CONFIRMATION_PATH,
					name: PAGES_NAME.PAGE_CBS_EVENT_CONFIRMATION_NAME,
					component: () => import("./views/apps/cbs/event/EventConfirmation.vue"),
					meta: {
						authRequired: true,
						pageTitle: "EventConfirmation",
						rule: "member"
					}
				},
				// =============================================================================
				// Activities
				// =============================================================================
				{
					path: PAGES_PATH.PAGE_CBS_ACTIVITY_PATH,
					name: PAGES_NAME.PAGE_CBS_ACTIVITY_PATH,
					component: () => import("./views/apps/cbs/activity/ActivityManagement.vue"),
					meta: {
						permissionKey: constant.PERMISSION.EMPLOYEE.VIEW_ALL,
						authRequired: true,
						pageTitle: "Activities",
						rule: "member"
					}
				}
			]
		},

		// =============================================================================
		// FULL PAGE LAYOUTS
		// =============================================================================
		{
			path: "",
			component: () => import("./layouts/full-page/FullPage.vue"),
			children: [
				// =============================================================================
				// Change role
				// =============================================================================
				{
					path: '/pages/change-role',
					name: "change-role",
					component: () => import("./views/pages/profile/ChangeRole.vue"),
					meta: {
						authRequired: true,
						pageTitle: "Change role",
						rule: "member"
					}
				},
				// =============================================================================
				// PAGES
				// =============================================================================
				{
					path: pages_login_path,
					name: "page-login",
					component: () => import("./views/pages/login/Login.vue"),
					meta: {
						rule: "guest"
					}
				},
				{
					path: pages_cbs_login_path,
					name: PAGES_NAME.PAGE_CBS_LOGIN_NAME,
					component: () => import("./views/pages/login/LoginCbs.vue"),
					meta: {
						rule: "guest"
					}
				},

				{
					path: pages_cmd_view_2025_path,
					name: PAGES_NAME.PAGE_CMD_VIEW_2025_NAME,
					component: () => import("./views/pages/ViewForm2025.vue"),
					meta: {
						rule: "member"
					}
				},

				{
					path: pages_tuyen_sinh_path,
					name: PAGES_NAME.PAGE_CMD_2025_NAME,
					component: () => import("./views/pages/TuyenSinh2025.vue"),
					meta: {
						rule: "member"
					}
				},

				{
					path: "/pages/register",
					name: "page-register",
					component: () => import("./views/pages/register/Register.vue"),
					meta: {
						rule: "guest"
					}
				},
				{
					path: "/pages/forgot-password",
					name: "page-forgot-password",
					component: () => import("./views/pages/ForgotPassword.vue"),
					meta: {
						rule: "guest"
					}
				},
				{
					path: "/pages/reset-password",
					name: "page-reset-password",
					component: () => import("./views/pages/ResetPassword.vue"),
					meta: {
						rule: "guest"
					}
				},
				{
					path: "/pages/lock-screen",
					name: "page-lock-screen",
					component: () => import("./views/pages/LockScreen.vue"),
					meta: {
						authRequired: true,
						rule: "member"
					}
				},
				{
					path: "/pages/comingsoon",
					name: "page-coming-soon",
					component: () => import("./views/pages/ComingSoon.vue"),
					meta: {
						rule: "*"
					}
				},
				{
					path: "/pages/error-404",
					name: "page-error-404",
					component: () => import("./views/pages/Error404.vue"),
					meta: {
						rule: "*"
					}
				},
				{
					path: "/pages/error-500",
					name: "page-error-500",
					component: () => import("./views/pages/Error500.vue"),
					meta: {
						rule: "*"
					}
				},
				{
					path: "/pages/not-authorized",
					name: "page-not-authorized",
					component: () => import("./views/pages/NotAuthorized.vue"),
					meta: {
						rule: "*"
					}
				},
				{
					path: "/pages/maintenance",
					name: "page-maintenance",
					component: () => import("./views/pages/Maintenance.vue"),
					meta: {
						rule: "*"
					}
				},
				// =============================================================================
				// Landing Page Register
				// =============================================================================
				{
					path: current_app_register_path,
					name: "Landing",
					component: () => import("./views/pages/register/RegisterLanding.vue"),
					meta: {
						pageTitle: "Landing",
						rule: "guest"
					}
				},
			]
		},
		// Redirect to 404 page, if no match found
		{
			path: "*",
			redirect: "/pages/error-404"
		}
	]
});

router.afterEach(() => {
	// Remove initial loading
	const appLoading = document.getElementById("loading-bg");
	if (appLoading) {
		appLoading.style.display = "none";
	}
});

router.beforeEach(async (to, from, next) => {
	if (to.path === pages_login_path || to.path === current_app_login_path) {
		if (store.state.auth.isUserLoggedIn()) {
			return router.push({
				path: current_app_path
			});
		}

		if (to.path === pages_login_path) {
			// Redirect into current app
			if (current_app_path && current_app_login_path !== from.path) {
				return router.push({
					path: current_app_login_path,
					query: { to: to.query.to || from.path }
				});
			} else {
				return;
			}
		}
	}

	// If auth required, check login. If login fails redirect to login page
	if (to.meta.authRequired) {
		if (!store.state.auth.isUserLoggedIn()) {
			return router.push({
				path: pages_login_path,
				query: { to: to.path }
			});
		}

		if (store.state.auth.userPermissions === null) {
			await store.dispatch("auth/fetchUserInfo", store.state.AppActiveUser.employee_id);
			try {
				await store.dispatch("auth/fetchUserPermissions", {});
			} catch (e) {
				return await router.push({ path: pages_login_path });
			}
		}

		if (to.meta.permissionKey && !store.getters["auth/hasPermission"](to.meta.permissionKey)) {
			return router.push({ path: "/pages/not-authorized" });
		}
	}

	return next();
	// Specify the current path as the customState parameter, meaning it
	// will be returned to the application after auth
	// auth.login({ target: to.path });
});

export default router;
